import cx from 'clsx';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import IconEyeHiddenOutlined from '@repo/monochrome-icons/icon-eye-hidden-outlined';
import IconEyeOutlined from '@repo/monochrome-icons/icon-eye-outlined';
import IconWarningRoundedFilled from '@repo/monochrome-icons/icon-warning-rounded-filled';
import { Button } from '#components/button/button';
import styles from './pin-code.module.css';

interface PinCodeBaseProps {
  length?: 4 | 6;
  value: string;
  onComplete?: (value: string) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  isMaskable?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  'data-testid'?: string;
}

export type PinCodeProps =
  | (PinCodeBaseProps & { isDisabled?: false; onChange: (value: string) => void })
  | (PinCodeBaseProps & { isDisabled: true; onChange?: (value: string) => void });

const NUMERIC_RE = /^[0-9]$/;

export function PinCode({
  length = 4,
  value,
  onChange,
  onComplete,
  isDisabled = false,
  isInvalid = false,
  errorMessage,
  isMaskable = false,
  autoFocus = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  className,
  'data-testid': testId,
}: PinCodeProps): ReactElement {
  const { formatMessage } = useIntl();
  const id = useId();
  const errorId = `${id}-error`;
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array.from({ length }, () => null));
  const [isMasked, setIsMasked] = useState(isMaskable);

  const isValidChar = useCallback((char: string): boolean => {
    return NUMERIC_RE.test(char);
  }, []);

  // Sync uncontrolled DOM inputs with the controlled value prop.
  // value is now a plain digit string with no sentinels: "12" means cells 0–1
  // are filled and cells 2–(length-1) are empty.
  useEffect(() => {
    for (let i = 0; i < length; i++) {
      const el = inputRefs.current[i];
      if (el) {
        const expected = value[i] ?? '';
        if (el.value !== expected) {
          el.value = expected;
        }
      }
    }
  }, [value, length]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number): void => {
    const { key } = e;

    if (key === 'Backspace') {
      e.preventDefault();
      if (value.length === 0) return;
      if (index >= value.length) {
        // Focused on an empty cell: remove the last filled digit and land on it.
        const newValue = value.slice(0, -1);
        onChange?.(newValue);
        inputRefs.current[newValue.length]?.focus();
      } else {
        // Focused on a filled cell: remove its digit and slide the rest left.
        const newValue = value.slice(0, index) + value.slice(index + 1);
        onChange?.(newValue);
      }
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      if (index > 0) inputRefs.current[index - 1]?.focus();
      return;
    }

    if (key === 'ArrowRight') {
      e.preventDefault();
      if (index < length - 1) inputRefs.current[index + 1]?.focus();
      return;
    }

    if (key === 'Home') {
      e.preventDefault();
      inputRefs.current[0]?.focus();
      return;
    }

    if (key === 'End') {
      e.preventDefault();
      inputRefs.current[length - 1]?.focus();
      return;
    }

    // Single printable character — guard against Ctrl+V / Cmd+V
    if (key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (!isValidChar(key)) return;

      if (value[index]) {
        // Focused cell is already filled: replace its digit in place.
        const newValue = value.slice(0, index) + key + value.slice(index + 1);
        onChange?.(newValue);
        const el = inputRefs.current[index];
        if (el) el.value = key;
        if (index < length - 1) {
          inputRefs.current[index + 1]?.focus();
        } else if (newValue.length === length) {
          onComplete?.(newValue);
        }
      } else {
        // Focused cell is empty: always fill the first empty slot sequentially.
        if (value.length >= length) return;
        const newValue = value + key;
        onChange?.(newValue);
        // Imperatively update the target cell (value.length, not index).
        const targetEl = inputRefs.current[value.length];
        if (targetEl) targetEl.value = key;
        if (newValue.length < length) {
          inputRefs.current[newValue.length]?.focus();
        } else {
          onComplete?.(newValue);
        }
      }
    }
  };

  // Mobile/IME path: onKeyDown key may be "Unidentified".
  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number): void => {
    const inputValue = e.target.value;
    if (!inputValue) return;

    const char = inputValue.slice(-1);
    if (!isValidChar(char)) {
      e.target.value = value[index] ?? '';
      return;
    }

    if (value[index]) {
      // Replace the digit in the focused filled cell.
      const newValue = value.slice(0, index) + char + value.slice(index + 1);
      onChange?.(newValue);
      e.target.value = char;
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      } else if (newValue.length === length) {
        onComplete?.(newValue);
      }
    } else {
      // Fill the first empty slot sequentially.
      if (value.length >= length) {
        e.target.value = value[index] ?? '';
        return;
      }
      const newValue = value + char;
      onChange?.(newValue);
      e.target.value = value[index] ?? '';
      if (newValue.length < length) {
        inputRefs.current[newValue.length]?.focus();
      } else {
        onComplete?.(newValue);
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const newDigits = [...text].filter(isValidChar).join('').slice(0, length);
    if (newDigits.length === 0) return;

    onChange?.(newDigits);

    const nextFocus = Math.min(newDigits.length, length - 1);
    inputRefs.current[nextFocus]?.focus();

    if (newDigits.length === length) {
      onComplete?.(newDigits);
    }
  };

  return (
    <div
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={cx(styles.group, className)}
      data-testid={testId}
      role="group"
    >
      <div className={styles.header}>
        <div className={styles.cells}>
          {Array.from({ length }, (_, i) => (
            <input
              aria-describedby={isInvalid && errorMessage ? errorId : undefined}
              aria-invalid={isInvalid || undefined}
              aria-label={formatMessage(
                { id: 'design-system-kit.pin-code.cell' },
                { index: i + 1, total: length }
              )}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              autoFocus={i === 0 ? autoFocus : undefined}
              className={cx(styles.cell, isInvalid && styles.cellError)}
              data-testid={testId ? `${testId}-cell-${i}` : undefined}
              defaultValue={value[i] ?? ''}
              disabled={isDisabled}
              inputMode="numeric"
              key={i}
              maxLength={2}
              name={`${id}-cell-${i}`}
              onChange={e => {
                handleChange(e, i);
              }}
              onFocus={e => {
                const el = e.target;
                requestAnimationFrame(() => {
                  if (document.activeElement === el) el.select();
                });
              }}
              onKeyDown={e => {
                handleKeyDown(e, i);
              }}
              onPaste={e => {
                handlePaste(e);
              }}
              ref={el => {
                inputRefs.current[i] = el;
              }}
              type={isMasked ? 'password' : 'text'}
            />
          ))}
        </div>
        {isMaskable ? (
          <Button
            aria-label={formatMessage({
              id: isMasked
                ? 'design-system-kit.pin-code.show-pin'
                : 'design-system-kit.pin-code.hide-pin',
            })}
            iconOnly
            isDisabled={isDisabled}
            onPress={() => {
              setIsMasked(m => !m);
            }}
            variant="tertiary"
          >
            {isMasked ? <IconEyeOutlined /> : <IconEyeHiddenOutlined />}
          </Button>
        ) : null}
      </div>
      {isInvalid && errorMessage ? (
        <p className={styles.errorMessage} id={errorId} role="alert">
          <IconWarningRoundedFilled className={styles.errorIcon} />
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
